import { SelectChangeEvent } from "@mui/material";
import { useEffect, useState } from "react";
import styled from "styled-components";
import TableView, { ColumnConfig, FetchParams } from "../../components/controls/table-view";
import Checkbox from "../../components/controls/checkbox";
import Dropdown from "../../components/controls/dropdown";
import SearchDropdown from "../../components/controls/search-dropdown";
import { TableContainer, TextContainer } from "../../styles/shared/table";
import { useAppDispatch } from "../../hooks/useAppDispatch";
import { useAppSelector } from "../../hooks/useAppSelector";
import {
  fetchReturns,
  resetReturns,
  updateReturn,
} from "../../store/features/returns/returns-slice";
import { ApplicationReturn, ReturnStatus } from "../../models/returns/applicant-return";
import { setCurrentPage } from "../../store/features/authentication/user-slice";
import { fetchBranches } from "../../store/features/organisation/branch-slice";
import { ApplicationSupplierHeader } from "../../models/supplier/application-supplier-header";
import { fetchSuppliers, resetSuppliers } from "../../store/features/supplier/supplier-slice";

export default function AdminReturns() {
  const dispatch = useAppDispatch();
  const returnsState = useAppSelector((state) => state.returns);
  const branchState = useAppSelector((state) => state.branch);
  const supplierState = useAppSelector((state) => state.supplier);
  const [statusFilter, setStatusFilter] = useState<"All" | ReturnStatus>("All");
  const [branchFilter, setBranchFilter] = useState<number | undefined>();
  const [supplierFilter, setSupplierFilter] = useState<number | undefined>();

  useEffect(() => {
    dispatch(setCurrentPage("Returns Worklist"));
    if (!branchState.branches) {
      dispatch(fetchBranches());
    }

    return () => {
      dispatch(resetReturns());
      dispatch(resetSuppliers());
    };
  }, [dispatch, branchState.branches]);

  const fetchMoreData = async (fetchParams: FetchParams) => {
    await dispatch(
      fetchReturns({
        fetchParams,
        userOnly: false,
        status: statusFilter === "All" ? undefined : statusFilter,
        supplierId: supplierFilter,
        branchId: branchFilter,
      })
    );
  };

  const handleStatusFilterChange = async (event: SelectChangeEvent<unknown>) => {
    const value = event.target.value as "All" | ReturnStatus;
    setStatusFilter(value);
    await dispatch(resetReturns());
    await dispatch(
      fetchReturns({
        fetchParams: { reset: true },
        userOnly: false,
        status: value === "All" ? undefined : value,
        supplierId: supplierFilter,
        branchId: branchFilter,
      })
    );
  };

  const handleBranchFilterChange = async (event: SelectChangeEvent<unknown>) => {
    const value = event.target.value as string;
    setBranchFilter(value === "All" ? undefined : Number(value));
    await dispatch(resetReturns());
    await dispatch(
      fetchReturns({
        fetchParams: { reset: true },
        userOnly: false,
        status: statusFilter === "All" ? undefined : statusFilter,
        supplierId: supplierFilter,
        branchId: value === "All" ? undefined : Number(value),
      })
    );
  };

  const fetchSupplierOptions = async (query: string, reset: boolean = true): Promise<void> => {
    await dispatch(fetchSuppliers({ fetchParams: { reset, searchText: query } }));
  };

  const handleSupplierFilterChange = async (supplier: ApplicationSupplierHeader | null) => {
    setSupplierFilter(supplier?.id);
    await dispatch(resetReturns());
    await dispatch(
      fetchReturns({
        fetchParams: { reset: true },
        userOnly: false,
        status: statusFilter === "All" ? undefined : statusFilter,
        supplierId: supplier?.id,
        branchId: branchFilter,
      })
    );
  };

  const renderFilterBar = () => (
    <FilterRow>
      <FilterContainer>
        <Dropdown
          id="cboReturnsWorklistStatus"
          label="Status"
          variant="outlined"
          autoWidth={false}
          value={statusFilter}
          onChange={handleStatusFilterChange}
          options={[
            { value: "All", label: "All" },
            { value: "New", label: "New" },
            { value: "Resolved", label: "Resolved" },
          ]}
          multiple={false}
          native={false}
          fullWidth={false}
        />
      </FilterContainer>
      <FilterContainer>
        <Dropdown
          id="cboReturnsWorklistBranch"
          label="Branch"
          variant="outlined"
          autoWidth={false}
          value={branchFilter ?? "All"}
          onChange={handleBranchFilterChange}
          options={[
            { value: "All", label: "All" },
            ...((branchState.branches ?? []).map((branch) => ({
              value: String(branch.id),
              label: branch.name,
            }))),
          ]}
          multiple={false}
          native={false}
          fullWidth={false}
        />
      </FilterContainer>
      <SupplierFilterContainer>
        <SearchDropdown<ApplicationSupplierHeader>
          fetchOptions={fetchSupplierOptions}
          clearOptions={() => dispatch(resetSuppliers())}
          options={supplierState.suppliers?.suppliers ?? []}
          hasMore={(supplierState.suppliers?.currentPosition ?? 0) < (supplierState.suppliers?.count ?? 0)}
          label="Supplier"
          onChange={handleSupplierFilterChange}
          isOptionEqualToValue={(option, value) => option.id == value.id}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              {option.name}
            </li>
          )}
        />
      </SupplierFilterContainer>
    </FilterRow>
  );

  const updateStatus = async (row: ApplicationReturn, resolved: boolean) => {
    const payload: ApplicationReturn = {
      id: row.id,
      branchId: row.branchId,
      branchName: row.branchName,
      supplierId: row.supplierId,
      supplierName: row.supplierName,
      supplierProductId: row.supplierProductId,
      productName: row.productName,
      returnQuantity: row.returnQuantity,
      notes: row.notes,
      status: resolved ? "Resolved" : "New",
      created: row.created,
      createdBy: row.createdBy,
      updated: new Date(),
      updatedBy: row.updatedBy,
    };

    await dispatch(
      updateReturn(payload)
    );
  };

  const columnConfig: { [key: string]: ColumnConfig } = {
    id: {
      hidden: true,
    },
    supplierId: {
      hidden: true,
    },
    supplierProductId: {
      hidden: true,
    },
    branchId: {
      hidden: true,
    },

    status: {
      name: "",
      order: 1,
      align: "center",
      render: (_value: string, row: { [key: string]: any }) => (
        <StatusCell>
          <Checkbox
            label=""
            checked={row.status === "Resolved"}
            onChange={(event) => updateStatus(row as ApplicationReturn, event.target.checked)}
          />
        </StatusCell>
      ),
    },
    branchName: {
    name: "Branch",
    order:2,
    render: (value: string | null) => <TextContainer>{value ?? "-"}</TextContainer>,
    },
    supplierName: {
      name: "Supplier",
      order: 3,
      render: (value: string | null) => <TextContainer>{value ?? "-"}</TextContainer>,
    },
    productName: {
      name: "Product",
      order: 4,
      render: (value: string | null) => <TextContainer>{value ?? "-"}</TextContainer>,
    },
    returnQuantity: {
      name: "Qty",
      order: 5,
      align: "center",
      render: (value: number) => <TextContainer>{value}</TextContainer>,
    },
    className: 
    { 
        hidden: true,
    },
    notes: {
      name: "Notes",
      order: 6,
      hideOnMobile: true,
      render: (value: string | null) => <TextContainer>{value ?? "-"}</TextContainer>,
    },
    createdBy: {
      name: "Created By",
      order: 7,
      hideOnMobile: true,
      render: (value: string | null) => <TextContainer>{value ?? "-"}</TextContainer>,
    },
    created: {
      name: "Created",
      order: 8,
      hideOnMobile: true,
      render: (value: Date) => (
        <TextContainer>
          {new Date(value).toLocaleString("en-gb", {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "numeric",
            minute: "numeric",
          })}
        </TextContainer>
      ),
    },
     
    updatedBy: {
      hidden: true,
    },
    updated: {
      hidden: true,
    },
  };

  return (
    <Container>
      <TableContainer>
        <TableView
          id="returnsWorklistTable"
          emptyText="No returns"
          fetchData={fetchMoreData}
          filterBar={renderFilterBar}
          totalCount={returnsState.returns?.count ?? 0}
          data={(returnsState.returns?.returns ?? []).map((row) => ({
            ...row,
            className: row.status === "Resolved" ? "resolved-row" : "",
          }))}
          columnConfig={columnConfig}
          showHeaders={true}
          search={true}
        />
      </TableContainer>
    </Container>
  );
}

const Container = styled.div`
  height: 95%;

  #returnsWorklistTable .resolved-row,
  #returnsWorklistTable .resolved-row:hover {
    background: ${(props) => props.theme.application.hover};
  }
`;

const StatusCell = styled.div`
  display: flex;
  justify-content: center;
`;

const FilterContainer = styled.div`
  min-width: 180px;
`;

const SupplierFilterContainer = styled.div`
  min-width: 300px;
`;

const FilterRow = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
`;
